import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Crown, Gem, Loader2, Sparkles, X, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DetailStatsGrid } from "@/components/DetailStatsGrid";
import {
  CONSUMER_STRIPE_PRICE_IDS,
  CONSUMER_TIER_LABELS,
  PAID_CONSUMER_TIERS,
  daysUntil,
  formatConsumerPrice,
  formatSubscriptionDate,
  isConsumerTier,
  isPaidConsumerTier,
  type ConsumerTier,
  type PaidConsumerTier,
} from "@/lib/consumerTier";
import {
  consumerEntitlementsQueryKey,
  isComplimentaryEliteSource,
} from "@/lib/consumerEntitlements";
import {
  cancelSubscription,
  consumerSubscriptionQueryKey,
  createCheckoutSession,
  fetchActiveSubscription,
  updateSubscriptionTier,
} from "@/lib/stripe";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type AboModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  /** When true, user cannot dismiss without subscribing (expired trial / lapsed). */
  required?: boolean;
  /** Escape hatch: open support / feedback (subscription-independent). */
  onContactSupport?: () => void;
  /** Escape hatch: navigate to account settings (subscription-independent). */
  onManageAccount?: () => void;
  /** Escape hatch: open account deletion flow (subscription-independent). */
  onDeleteAccount?: () => void;
};

type ModalView = "main" | "confirm-switch" | "confirm-cancel";

const TIER_ICONS: Record<PaidConsumerTier, typeof Sparkles> = {
  member: Sparkles,
  circle_plus: Zap,
  elite: Crown,
};

/** High-level benefit keys per tier — copy must match CONSUMER_ENTITLEMENTS_BY_TIER + Live Radar gate. */
const TIER_BENEFIT_KEYS: Record<PaidConsumerTier, readonly string[]> = {
  member: ["searchRadius", "eventHorizon", "oneCircle", "interested"],
  circle_plus: ["liveRadar", "unlimitedSocial", "discoveryBoost", "earlyAccess"],
  elite: ["aiConcierge", "unlimitedDiscovery", "earlyAccessElite", "priorityExtras", "unlimitedMemories"],
};

export function AboModal({
  open,
  onOpenChange,
  userId,
  required = false,
  onContactSupport,
  onManageAccount,
  onDeleteAccount,
}: AboModalProps) {
  const { t } = useTranslation("consumer");
  const queryClient = useQueryClient();
  const [view, setView] = useState<ModalView>("main");
  const [pendingTier, setPendingTier] = useState<PaidConsumerTier | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: subscription, isLoading, refetch } = useQuery({
    queryKey: consumerSubscriptionQueryKey(userId),
    queryFn: () => fetchActiveSubscription("user", userId),
    enabled: open && !!userId,
  });

  useEffect(() => {
    if (!open) {
      setView("main");
      setPendingTier(null);
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const currentTier =
    subscription?.tier && isConsumerTier(subscription.tier) ? subscription.tier : null;
  const periodEnd = subscription?.current_period_end;
  const isTrialing = subscription?.status === "trialing";
  const trialEnd = subscription?.trial_end;

  const isComplimentary = isComplimentaryEliteSource(subscription?.subscription_source);

  function handleClose() {
    if (busy || required) return;
    onOpenChange(false);
  }

  async function invalidateSubscription() {
    await queryClient.invalidateQueries({ queryKey: consumerSubscriptionQueryKey(userId) });
    await queryClient.invalidateQueries({ queryKey: consumerEntitlementsQueryKey(userId) });
    await refetch();
  }

  async function handleSubscribe(tier: PaidConsumerTier) {
    setBusy(true);
    setError(null);
    try {
      await createCheckoutSession({
        ownerType: "user",
        ownerId: userId,
        priceId: CONSUMER_STRIPE_PRICE_IDS[tier],
        tier,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("subscription.checkoutFailed"));
      setBusy(false);
    }
  }

  async function handleConfirmSwitch() {
    if (!pendingTier) return;
    setBusy(true);
    setError(null);
    try {
      const { effectiveDate } = await updateSubscriptionTier(pendingTier);
      const label = CONSUMER_TIER_LABELS[pendingTier];
      const dateLabel = effectiveDate
        ? formatSubscriptionDate(effectiveDate)
        : t("subscription.periodEnd");
      toast.success(t("subscription.switchScheduled", { tier: label, date: dateLabel }));
      await invalidateSubscription();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("subscription.switchFailed"));
      setBusy(false);
    }
  }

  async function handleConfirmCancel() {
    if (!subscription?.stripe_subscription_id) return;
    setBusy(true);
    setError(null);
    try {
      await cancelSubscription(subscription.stripe_subscription_id);
      const dateLabel = periodEnd
        ? formatSubscriptionDate(periodEnd)
        : t("subscription.periodEnd");
      toast.success(t("subscription.cancelScheduled", { date: dateLabel }));
      await invalidateSubscription();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("subscription.cancelFailed"));
      setBusy(false);
    }
  }

  function handleTierPress(tier: PaidConsumerTier) {
    if (busy || tier === currentTier || isComplimentary) return;
    if (!subscription) {
      void handleSubscribe(tier);
      return;
    }
    setPendingTier(tier);
    setView("confirm-switch");
    setError(null);
  }

  const modalTitle =
    view === "confirm-switch"
      ? t("subscription.titleSwitch")
      : view === "confirm-cancel"
        ? t("subscription.titleCancel")
        : required
          ? t("subscription.requiredTitle")
          : t("subscription.title");

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />
      <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
        <div
          className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border border-border bg-background shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="abo-modal-title"
          data-testid="abo-modal"
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 id="abo-modal-title" className="text-base font-bold text-foreground">
              {modalTitle}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              disabled={busy || required}
              className={cn(
                "rounded-full p-1 text-muted-foreground transition-colors hover:bg-bg-surface disabled:opacity-40",
                required && "invisible",
              )}
              aria-label={t("subscription.close")}
              tabIndex={required ? -1 : 0}
            >
              <X size={18} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-pulse" />
              </div>
            ) : view === "confirm-switch" && pendingTier ? (
              <ConfirmSwitchView
                targetTier={pendingTier}
                periodEnd={periodEnd}
                error={error}
                busy={busy}
                onBack={() => {
                  setView("main");
                  setPendingTier(null);
                  setError(null);
                }}
                onConfirm={() => void handleConfirmSwitch()}
              />
            ) : view === "confirm-cancel" ? (
              <ConfirmCancelView
                periodEnd={periodEnd}
                error={error}
                busy={busy}
                onBack={() => {
                  setView("main");
                  setError(null);
                }}
                onConfirm={() => void handleConfirmCancel()}
              />
            ) : (
              <MainView
                subscription={subscription ?? null}
                currentTier={currentTier}
                isTrialing={isTrialing}
                trialEnd={trialEnd}
                periodEnd={periodEnd}
                busy={busy}
                required={required}
                isComplimentary={isComplimentary}
                isPlatformTrial={
                  isTrialing && subscription?.subscription_source === "platform"
                }
                onTierPress={handleTierPress}
                onCancelPress={() => {
                  setView("confirm-cancel");
                  setError(null);
                }}
              />
            )}
          </div>

          {required &&
            view === "main" &&
            !isLoading &&
            (onContactSupport || onManageAccount || onDeleteAccount) && (
              <div
                className="shrink-0 space-y-3 border-t border-border px-5 py-4"
                data-testid="abo-required-escape"
              >
                {onContactSupport && (
                  <div className="space-y-1.5">
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      {t("subscription.requiredSupportPrompt")}
                    </p>
                    <button
                      type="button"
                      data-testid="abo-contact-support"
                      disabled={busy}
                      onClick={onContactSupport}
                      className="text-sm font-semibold text-pulse underline-offset-2 hover:underline disabled:opacity-40"
                    >
                      {t("subscription.requiredSupportCta")}
                    </button>
                  </div>
                )}
                {(onManageAccount || onDeleteAccount) && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    {onManageAccount && (
                      <button
                        type="button"
                        data-testid="abo-manage-account"
                        disabled={busy}
                        onClick={onManageAccount}
                        className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-40"
                      >
                        {t("subscription.requiredManageAccount")}
                      </button>
                    )}
                    {onDeleteAccount && (
                      <button
                        type="button"
                        data-testid="abo-delete-account"
                        disabled={busy}
                        onClick={onDeleteAccount}
                        className="text-xs font-medium text-red-400/80 underline-offset-2 hover:text-red-400 hover:underline disabled:opacity-40"
                      >
                        {t("subscription.requiredDeleteAccount")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
        </div>
      </div>
    </>
  );
}

function MainView({
  subscription,
  currentTier,
  isTrialing,
  trialEnd,
  periodEnd,
  busy,
  required = false,
  isComplimentary = false,
  isPlatformTrial = false,
  onTierPress,
  onCancelPress,
}: {
  subscription: Awaited<ReturnType<typeof fetchActiveSubscription>>;
  currentTier: ConsumerTier | null;
  isTrialing: boolean;
  trialEnd: string | null | undefined;
  periodEnd: string | null | undefined;
  busy: boolean;
  required?: boolean;
  isComplimentary?: boolean;
  isPlatformTrial?: boolean;
  onTierPress: (tier: PaidConsumerTier) => void;
  onCancelPress: () => void;
}) {
  const { t } = useTranslation("consumer");
  const stats = [];

  if (currentTier) {
    stats.push({
      icon: <Sparkles className="h-3.5 w-3.5" />,
      label: t("subscription.stats.current"),
      value: CONSUMER_TIER_LABELS[currentTier],
      valueClassName: "text-pulse",
    });
    if (isPaidConsumerTier(currentTier)) {
      stats.push({
        icon: <Gem className="h-3.5 w-3.5" />,
        label: t("subscription.stats.price"),
        value: formatConsumerPrice(currentTier),
      });
    }
  }

  if (isTrialing && trialEnd) {
    stats.push({
      icon: <Crown className="h-3.5 w-3.5" />,
      label: t("subscription.stats.trial"),
      value: t("subscription.stats.trialRemaining", {
        days: daysUntil(trialEnd),
        date: formatSubscriptionDate(trialEnd),
      }),
    });
  } else if (periodEnd) {
    stats.push({
      icon: <Crown className="h-3.5 w-3.5" />,
      label: subscription?.cancel_at_period_end
        ? t("subscription.stats.ends")
        : t("subscription.stats.nextBilling"),
      value: formatSubscriptionDate(periodEnd),
    });
  }

  return (
    <div className="space-y-5">
      {required && !subscription && (
        <p className="text-sm text-muted-foreground">{t("subscription.requiredBody")}</p>
      )}
      {!required && !subscription && (
        <p className="text-sm text-muted-foreground">{t("subscription.intro")}</p>
      )}

      {subscription?.cancel_at_period_end && periodEnd && (
        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-muted-foreground">
          {t("subscription.cancelBanner", { date: formatSubscriptionDate(periodEnd) })}
        </div>
      )}

      {stats.length > 0 && <DetailStatsGrid items={stats} columns={stats.length >= 3 ? 3 : 2} />}

      {isPlatformTrial ? (
        <div className="rounded-2xl border border-pulse/25 bg-pulse/10 px-4 py-3 text-sm text-foreground">
          {t("subscription.doubleRequestsUpsell")}
        </div>
      ) : null}

      {!isComplimentary && (
      <div>
        <p className="mb-3 text-[10px] font-semibold uppercase tracking-widest text-text-subtle">
          {subscription ? t("subscription.switchPlan") : t("subscription.choosePlan")}
        </p>
        <div className="space-y-3">
          {PAID_CONSUMER_TIERS.map((tier) => {
            const isCurrent = tier === currentTier;
            const Icon = TIER_ICONS[tier];

            return (
              <button
                key={tier}
                type="button"
                data-testid={`abo-tier-${tier}-cta`}
                disabled={busy || isCurrent}
                onClick={() => onTierPress(tier)}
                className={cn(
                  "relative w-full rounded-2xl border p-4 text-left transition-all",
                  isCurrent
                    ? "cursor-default border-pulse/50 bg-pulse/5 shadow-pulse"
                    : "border-border bg-bg-surface hover:border-pulse/30 active:scale-[0.99]",
                  busy && !isCurrent && "opacity-60",
                )}
              >
                {isCurrent && (
                  <span className="absolute -top-2 left-4 inline-flex rounded-full bg-gradient-pulse px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-pulse-foreground">
                    {t("subscription.currentBadge")}
                  </span>
                )}

                <span className="flex items-start gap-3">
                  <span
                    className={cn(
                      "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
                      isCurrent ? "bg-pulse/15 text-pulse" : "bg-bg-surface-hover text-muted-foreground",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="block text-sm font-bold text-foreground">{CONSUMER_TIER_LABELS[tier]}</span>
                      <span className="inline-flex rounded-full border border-pulse/20 bg-pulse/5 px-2 py-0.5 text-[10px] font-semibold text-pulse">
                        {formatConsumerPrice(tier)}
                      </span>
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {t(`subscription.taglines.${tier}`)}
                    </span>
                    <ul className="mt-2.5 space-y-1.5" data-testid={`abo-tier-${tier}-benefits`}>
                      {TIER_BENEFIT_KEYS[tier].map((key) => (
                        <li
                          key={key}
                          className="flex gap-2 text-[11px] leading-snug text-muted-foreground"
                        >
                          <Check className="mt-0.5 h-3 w-3 shrink-0 text-pulse" aria-hidden />
                          <span>{t(`subscription.benefits.${tier}.${key}`)}</span>
                        </li>
                      ))}
                    </ul>
                  </span>
                  {!isCurrent && (
                    <span className="shrink-0 self-center text-xs font-semibold text-pulse">
                      {subscription ? t("subscription.switch") : t("subscription.start")}
                    </span>
                  )}
                  {isCurrent && <Check className="mt-1 h-4 w-4 shrink-0 text-pulse" />}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      )}

      {subscription && !subscription.cancel_at_period_end && !required && !isComplimentary && subscription.stripe_subscription_id && (
        <div className="border-t border-border pt-4">
          <button
            type="button"
            data-testid="abo-cancel-link"
            disabled={busy}
            onClick={onCancelPress}
            className="text-xs font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-red-400/80 hover:underline disabled:opacity-40"
          >
            {t("subscription.cancelLink")}
          </button>
        </div>
      )}
    </div>
  );
}

function ConfirmSwitchView({
  targetTier,
  periodEnd,
  error,
  busy,
  onBack,
  onConfirm,
}: {
  targetTier: PaidConsumerTier;
  periodEnd: string | null | undefined;
  error: string | null;
  busy: boolean;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation("consumer");
  const dateLabel = periodEnd
    ? formatSubscriptionDate(periodEnd)
    : t("subscription.periodEndFallback");

  return (
    <div className="space-y-5">
      <p className="text-sm leading-relaxed text-muted-foreground">
        {t("subscription.confirmSwitchBody", {
          tier: CONSUMER_TIER_LABELS[targetTier],
          date: dateLabel,
        })}
      </p>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="abo-confirm-switch"
          onClick={onConfirm}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-pulse px-4 py-2.5 text-sm font-semibold text-pulse-foreground shadow-pulse disabled:opacity-40"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("subscription.confirmSwitch")}
        </button>
        <button
          type="button"
          data-testid="abo-confirm-back"
          onClick={onBack}
          disabled={busy}
          className="rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-bg-surface disabled:opacity-40"
        >
          {t("subscription.back")}
        </button>
      </div>
    </div>
  );
}

function ConfirmCancelView({
  periodEnd,
  error,
  busy,
  onBack,
  onConfirm,
}: {
  periodEnd: string | null | undefined;
  error: string | null;
  busy: boolean;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation("consumer");
  const dateLabel = periodEnd
    ? formatSubscriptionDate(periodEnd)
    : t("subscription.periodEndFallback");

  return (
    <div className="space-y-5">
      <p className="text-sm leading-relaxed text-muted-foreground">
        {t("subscription.confirmCancelBody", { date: dateLabel })}
      </p>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="abo-confirm-cancel"
          onClick={onConfirm}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-xl bg-red-500/90 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-500 disabled:opacity-40"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("subscription.confirmCancel")}
        </button>
        <button
          type="button"
          data-testid="abo-confirm-back"
          onClick={onBack}
          disabled={busy}
          className="rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:bg-bg-surface disabled:opacity-40"
        >
          {t("subscription.back")}
        </button>
      </div>
    </div>
  );
}
